import React from 'react';
import { MapPin, Phone, Mail, Star, Heart, Package, Sparkles, Users, Clock, Palette } from 'lucide-react';

export const AboutUs = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-pink-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">About Shynerz Baby Shop</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Creating unique and functional spaces for children to express their personalities
            </p>
          </div>
        </div>
      </div>

      {/* Our Story */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="prose prose-pink">
                <p className="text-gray-600 mb-4">
                  Welcome to Shynerz Baby Shop. Our journey began a few years ago with a simple idea of creating a place where children can find clothes, furniture and decor that not only look great but also allow them to express their unique personalities.
                </p>
                <p className="text-gray-600 mb-4">
                  As parents, we understand how important it is for kids to feel confident and comfortable in their outfit whether playing or attending a family function. Moreso, in how their bedrooms, play and study areas appear to them.
                </p>
                <p className="text-gray-600">
                  Out of this idea, Shynerz Baby Shop was started both as an online and physical shop dealing with all kids' collections, furniture and interiors. We provide a wide range of kids' collections which include clothing, feeding, playtime among other essentials from newborn to 8 years.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1519689680058-324335c77eba"
                alt="Happy baby"
                className="rounded-lg shadow-lg"
              />
              <img
                src="https://images.unsplash.com/photo-1596461404969-9ae70f2830c1"
                alt="Baby products"
                className="rounded-lg shadow-lg mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-pink-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Star className="h-8 w-8 text-pink-500" />
                <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-600">
                To be the brand of choice for unique and functional kids' fashion, furniture and interiors. We believe every child deserves the freedom to express themselves uniquely while staying confident and comfortable. While committed to community and sustainability, we work to create a world where kids can explore, play and grow, all while wearing outfits that are equally vibrant as their dreams and imaginations.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="h-8 w-8 text-pink-500" />
                <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-600">
                To see fashion & interior design play a part in kids' smart future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <Package className="h-10 w-10 text-pink-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Quality</h3>
              <p className="text-gray-600">
                We believe in providing every baby the best. Our clothes are made from high quality fabric that is long lasting and comfortable. We assure you of consistent quality.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <Palette className="h-10 w-10 text-pink-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Creativity</h3>
              <p className="text-gray-600">
                Self expressions and individuality are what we celebrate each day. Our designs are vibrant and playful, encouraging children to explore their imaginations and unique styles.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <Users className="h-10 w-10 text-pink-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Inclusivity</h3>
              <p className="text-gray-600">
                We believe that every child needs to feel respected, represented and valued. Our clothes are designed for all shapes, backgrounds and ages from newborn to 8 years.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <Clock className="h-10 w-10 text-pink-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Efficiency</h3>
              <p className="text-gray-600">
                We understand the excitement children have on receiving new items. We process your order fast and strive to deliver within the agreed on timeline.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Visit Our Store</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-6 w-6 text-pink-500" />
                    <p className="text-gray-600">
                    Crystal Center at Thindugua Stage, Kiambu Road
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-6 w-6 text-pink-500" />
                    <p className="text-gray-600">+254 759 409139</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-6 w-6 text-pink-500" />
                    <p className="text-gray-600">sales.shynerz@gmail.com</p>
                  </div>
                </div>
                <div className="h-64 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.0176601669745!2d36.8558!3d-1.2088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMcKwMTInMzEuNyJTIDM2wrA1MScyMC45IkU!5e0!3m2!1sen!2sus!4v1624901845678!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};